/**
 * Confirm Dialog Component
 *
 * A reusable confirmation dialog with configurable title and message.
 * Provides confirm and cancel actions with customizable button text.
 *
 * @module components/ui/ConfirmDialog
 */

import React from 'react';
import { useTheme } from '@/hooks/useTheme';
import { Button } from '@/components/ui/Button';
import { SubtitleText } from '@/components/ui/SubtitleText';
import { BodyText } from '@/components/ui/BodyText';

export interface ConfirmDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Function to call when dialog should close */
  onClose: () => void;
  /** Function to call when user confirms */
  onConfirm: () => void;
  /** Dialog title */
  title: string;
  /** Dialog message */
  message: string;
  /** Text for confirm button (default: "Confirm") */
  confirmText?: string;
  /** Text for cancel button (default: "Cancel") */
  cancelText?: string;
  /** Whether the confirm action is loading */
  isLoading?: boolean;
  /** Variant for confirm button (default: "primary") */
  confirmVariant?: 'primary' | 'secondary' | 'accent' | 'ghost' | 'outline';
  /** Whether to style the confirm button as destructive/error */
  destructive?: boolean;
}

/**
 * Confirm Dialog Component
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
  confirmVariant = 'primary',
  destructive = false
}) => {
  const { isDark } = useTheme();

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className={`w-full max-w-md rounded-lg shadow-xl ${
        isDark ? 'bg-secondary-900' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`p-6 border-b ${
          isDark ? 'border-neutral-700' : 'border-neutral-200'
        }`}>
          <div className="flex items-center justify-between">
            <SubtitleText className="font-semibold">
              {title}
            </SubtitleText>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="p-2"
              disabled={isLoading}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <BodyText className="mb-6">
            {message}
          </BodyText>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              {cancelText}
            </Button>
            <Button
              variant={confirmVariant}
              onClick={onConfirm}
              disabled={isLoading}
              className={destructive ? 'bg-error-600 hover:bg-error-700 text-white border-error-600' : ''}
            >
              {isLoading ? 'Processing...' : confirmText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;